import { useState } from "react";
import { useEvent } from "@/context/event-context";
import { formatPrice, isSoldOut, remainingQuantity } from "@/lib/format";
import { Skeleton } from "@/components/ui/skeleton";
import { TicketSelector } from "@/components/checkout/TicketSelector";
import { TableCustomizer } from "@/components/checkout/TableCustomizer";
import { WaitlistForm } from "@/components/event/WaitlistForm";
import { Flame, Users, Wine } from "lucide-react";
import type { TicketTier, TablePackage } from "@/types/database";

/* ─── Shared sub-components ─── */

const ArrowIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="transition-transform group-hover:translate-x-1">
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);

const ScarcityBadge = ({ remaining, total }: { remaining: number; total: number }) => {
  const threshold = Math.max(Math.ceil(total * 0.3), 5);
  if (remaining > threshold || remaining <= 0) return null;
  return (
    <span className="inline-flex items-center gap-1.5 font-heading text-[0.65rem] uppercase tracking-[0.15em] font-extrabold text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1">
      <Flame className="w-3 h-3" />
      Only {remaining} left
    </span>
  );
};

/* ─── Types ─── */

interface DisplayItem {
  id: string;
  name: string;
  price: number;
  priceDisplay: string;
  desc: string;
  cta: string;
  soldOut: boolean;
  type: "ticket" | "table";
  tierType?: string;
  maxQuantity: number;
  remaining: number;
  totalAvailable: number;
  waitlistEnabled: boolean;
  seats?: number;
  bottleCount?: number;
}

/* ─── Individual Ticket Card ─── */

const TicketCard = ({ item, onSelect }: { item: DisplayItem; onSelect: (item: DisplayItem) => void }) => (
  <div className="border border-foreground/10 p-8 sm:p-10 flex flex-col justify-between h-full">
    <div>
      <p className="font-heading text-[0.7rem] uppercase tracking-[0.2em] font-extrabold text-foreground/70 mb-3">
        {item.name === "Early Bird" ? "Limited Release" : "Standard Entry"}
      </p>
      <h3 className="font-serif-elegant text-2xl sm:text-3xl text-foreground italic mb-2">{item.name}</h3>
      <p className="font-heading text-3xl sm:text-4xl font-extrabold tracking-[0.05em] text-foreground mb-4">
        {item.priceDisplay}
      </p>
      <p
        className="font-serif-elegant text-foreground/60 italic mb-6"
        style={{
          fontSize: "var(--body-size, 1.1rem)",
          lineHeight: "var(--body-line-height, 1.65)",
        }}
      >
        {item.desc}
      </p>
    </div>
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-3 flex-wrap">
        {item.soldOut ? (
          <span className="inline-flex items-center gap-2 font-heading text-[0.75rem] uppercase tracking-[0.2em] font-extrabold text-foreground/40">
            Sold Out
          </span>
        ) : (
          <>
            <button
              onClick={() => onSelect(item)}
              className="group inline-flex items-center gap-2 bg-ink text-white px-6 py-3 font-heading text-[0.75rem] uppercase tracking-[0.2em] font-extrabold hover:bg-foreground/80 transition-colors"
            >
              {item.cta} <ArrowIcon />
            </button>
            <ScarcityBadge remaining={item.remaining} total={item.totalAvailable} />
          </>
        )}
      </div>
      {item.soldOut && item.waitlistEnabled && (
        <WaitlistForm tierId={item.id} tierName={item.name} />
      )}
    </div>
  </div>
);

/* ─── Table Package Row ─── */

const TableRow = ({ item, onSelect }: { item: DisplayItem; onSelect: (item: DisplayItem) => void }) => (
  <div
    className={`border-b border-foreground/10 py-6 sm:py-8 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-4 items-center transition-opacity ${
      item.soldOut ? "opacity-60" : ""
    }`}
  >
    <div>
      <div className="flex items-center gap-3 mb-2 flex-wrap">
        <h3 className="font-serif-elegant text-xl sm:text-2xl text-foreground italic">{item.name}</h3>
        <span
          className={`font-heading text-xl sm:text-2xl font-extrabold tracking-[0.05em] text-foreground ${
            item.soldOut ? "line-through decoration-foreground/30" : ""
          }`}
        >
          {item.priceDisplay}
        </span>
      </div>
      <div className="flex items-center gap-4 text-foreground/70 font-heading text-[0.7rem] uppercase tracking-[0.15em] font-bold mb-2">
        {item.seats && (
          <span className="inline-flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5" />
            {item.seats} Guests
          </span>
        )}
        {item.bottleCount && item.bottleCount > 0 && (
          <span className="inline-flex items-center gap-1.5">
            <Wine className="w-3.5 h-3.5" />
            {item.bottleCount} {item.bottleCount === 1 ? "Bottle" : "Bottles"}
          </span>
        )}
      </div>
      <p
        className="font-serif-elegant text-foreground/60 italic max-w-[90%]"
        style={{
          fontSize: "var(--body-size, 1rem)",
          lineHeight: "var(--body-line-height, 1.55)",
        }}
      >
        {item.desc}
      </p>
    </div>
    <div className="flex items-center gap-3 flex-wrap">
      {item.soldOut ? (
        <>
          <span
            aria-label="Sold out"
            className="inline-flex items-center gap-2 bg-ink text-cream px-5 py-2.5 font-heading text-[0.75rem] uppercase tracking-[0.25em] font-extrabold border border-ink"
          >
            Sold Out
          </span>
          {item.waitlistEnabled && (
            <WaitlistForm tablePackageId={item.id} tierName={item.name} />
          )}
        </>
      ) : (
        <>
          <button
            onClick={() => onSelect(item)}
            className="group inline-flex items-center gap-2 bg-ink text-white px-6 py-3 font-heading text-[0.75rem] uppercase tracking-[0.2em] font-extrabold hover:bg-foreground/80 transition-colors whitespace-nowrap"
          >
            Reserve <ArrowIcon />
          </button>
          <ScarcityBadge remaining={item.remaining} total={item.totalAvailable} />
        </>
      )}
    </div>
  </div>
);

/* ─── Skeleton ─── */

const TicketsMenuSkeleton = () => (
  <section id="tickets" className="bg-white py-12 sm:py-[10vw] border-t border-ink">
    <div className="max-w-[1200px] mx-auto px-4 sm:px-[4vw]">
      <div className="text-center mb-12">
        <Skeleton className="h-4 w-40 mx-auto mb-4" />
        <Skeleton className="h-12 w-64 mx-auto" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-16">
        {[1, 2].map((i) => (
          <Skeleton key={i} className="h-64 w-full" />
        ))}
      </div>
      <Skeleton className="h-4 w-40 mx-auto mb-4" />
      <Skeleton className="h-12 w-64 mx-auto mb-8" />
      {[1, 2, 3, 4].map((i) => (
        <Skeleton key={i} className="h-24 w-full mb-4" />
      ))}
    </div>
  </section>
);

/* ─── Main Component ─── */

const TicketsMenu = () => {
  const { event, isLoading } = useEvent();
  const [selectedItem, setSelectedItem] = useState<DisplayItem | null>(null);
  const [selectedTablePkg, setSelectedTablePkg] = useState<TablePackage | null>(null);

  if (isLoading) return <TicketsMenuSkeleton />;
  if (!event) return null;

  const ticketTiers = (event.ticket_tiers || [])
    .filter((t: TicketTier) => t.active)
    .sort((a: TicketTier, b: TicketTier) => a.sort_order - b.sort_order);

  const tablePackages = (event.table_packages || [])
    .filter((p: TablePackage) => p.active)
    .sort((a: TablePackage, b: TablePackage) => a.sort_order - b.sort_order);

  const waitlistOn = event.waitlist_enabled ?? false;

  const ticketItems: DisplayItem[] = ticketTiers.map((tier: TicketTier) => ({
    id: tier.id,
    name: tier.name,
    price: tier.price,
    priceDisplay: formatPrice(tier.price),
    desc: tier.description || "",
    cta: "Purchase",
    soldOut: isSoldOut(tier.quantity_available, tier.quantity_sold),
    type: "ticket" as const,
    tierType: tier.tier_type,
    maxQuantity: Math.min(10, remainingQuantity(tier.quantity_available, tier.quantity_sold)),
    remaining: remainingQuantity(tier.quantity_available, tier.quantity_sold),
    totalAvailable: tier.quantity_available,
    waitlistEnabled: waitlistOn,
  }));

  const tableItems: DisplayItem[] = tablePackages.map((pkg: TablePackage) => ({
    id: pkg.id,
    name: pkg.name,
    price: pkg.price,
    priceDisplay: formatPrice(pkg.price),
    desc: pkg.includes_description || `Reserved seating for ${pkg.seats} guests.`,
    cta: "Reserve",
    soldOut: !!pkg.sold_out || isSoldOut(pkg.quantity_available, pkg.quantity_sold),
    type: "table" as const,
    maxQuantity: Math.min(3, remainingQuantity(pkg.quantity_available, pkg.quantity_sold)),
    remaining: remainingQuantity(pkg.quantity_available, pkg.quantity_sold),
    totalAvailable: pkg.quantity_available,
    waitlistEnabled: waitlistOn,
    seats: pkg.seats,
    bottleCount: pkg.bottle_count,
  }));

  return (
    <>
      <section id="tickets" data-nav-theme="light" className="bg-white py-12 sm:py-[10vw] border-t border-ink">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-[4vw]">
          {/* Section header */}
          <div className="text-center mb-12 sm:mb-16">
            <p
              className="font-heading uppercase text-foreground mb-4"
              style={{
                fontSize: "var(--label-size, 0.85rem)",
                fontWeight: "var(--label-weight, 800)",
                letterSpacing: "var(--label-tracking, 0.2em)",
              }}
            >
              Access & Reservations
            </p>
            <h2
              className="font-heading text-foreground"
              style={{
                fontSize: "var(--section-size, clamp(2rem, 4vw, 4rem))",
                fontWeight: "var(--section-weight, 800)",
                letterSpacing: "var(--section-tracking, 0.15em)",
                fontStyle: "var(--section-style, normal)" as any,
                lineHeight: 1.1,
                textTransform: "var(--hero-transform, uppercase)" as any,
              }}
            >
              The Collection
            </h2>
          </div>

          {/* ── Individual Tickets ── */}
          {ticketItems.length > 0 && (
            <div className="mb-16 sm:mb-20">
              <div className="flex items-center gap-4 mb-8">
                <div className="h-px flex-1 bg-foreground/10" />
                <p className="font-heading text-[0.7rem] uppercase tracking-[0.25em] font-extrabold text-foreground/70 shrink-0">
                  Individual Tickets
                </p>
                <div className="h-px flex-1 bg-foreground/10" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {ticketItems.map((item) => (
                  <TicketCard key={item.id} item={item} onSelect={setSelectedItem} />
                ))}
              </div>
            </div>
          )}

          {/* ── Table Service ── */}
          {tableItems.length > 0 && (
            <div>
              <div className="flex items-center gap-4 mb-8">
                <div className="h-px flex-1 bg-foreground/10" />
                <p className="font-heading text-[0.7rem] uppercase tracking-[0.25em] font-extrabold text-foreground/70 shrink-0">
                  Table Service
                </p>
                <div className="h-px flex-1 bg-foreground/10" />
              </div>
              <div>
                {tableItems.map((item) => (
                  <TableRow
                    key={item.id}
                    item={item}
                    onSelect={(di) => {
                      const pkg = tablePackages.find((p: TablePackage) => p.id === di.id);
                      if (pkg) setSelectedTablePkg(pkg);
                    }}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Ticket selector sheet */}
      {selectedItem && (
        <TicketSelector
          open={!!selectedItem}
          onOpenChange={(open) => !open && setSelectedItem(null)}
          item={{
            id: selectedItem.id,
            name: selectedItem.name,
            price: selectedItem.price,
            type: selectedItem.type,
            tierType: selectedItem.tierType,
            description: selectedItem.desc,
            maxQuantity: selectedItem.maxQuantity,
          }}
        />
      )}

      {/* Table customizer */}
      {selectedTablePkg && (
        <TableCustomizer
          open={!!selectedTablePkg}
          onOpenChange={(open) => !open && setSelectedTablePkg(null)}
          tablePackage={selectedTablePkg}
        />
      )}
    </>
  );
};

export default TicketsMenu;
